import {
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Switch,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconCategory,
  IconDeviceFloppy,
  IconLanguage,
  IconLayoutGrid,
  IconPlus,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createCategory, updateCategory } from "@/api/category";
import { translate } from "@/api/translate";
import { notify } from "@/lib/notify";
import { buildTranslations, flattenTranslations } from "@/lib/menuLang";
import { useMenuLanguages } from "@/hooks/useMenuLanguages";
import { TranslationFields } from "@/components/TranslationFields";
import { FormModalFooter } from "@/components/FormModalFooter";
import { FormSection } from "@/components/FormSection";

// Taban dil örneği. Çeviri alanlarının ipucu artık dile özel değil, KAYNAK metnin kendisi
// (bkz. components/TranslationFields) — dil listesi veriden geldiği için dile özel örnek tutulamaz.
const NAME_PLACEHOLDER = "Örn. Kebaplar";

export function CategoryFormModal({ opened, onClose, category }) {
  const { contentLanguages, base, baseCode } = useMenuLanguages();
  // Taban dil de şeritte: alanı ayrı dursaydı aynı iş iki kez yer kaplıyordu. Sırada ilk, çünkü
  // içeriğin kaynağı o ve zorunlu alan orada.
  const languages = base ? [base, ...contentLanguages] : contentLanguages;
  const [lang, setLang] = useState(null);
  const queryClient = useQueryClient();
  const isEdit = Boolean(category);

  const form = useForm({
    // Çeviri alanları BURADA sayılamaz: hangi diller açık olduğu veriden geliyor ve bu obje
    // bir kez değerlendiriliyor. Alanlar aşağıdaki effect'te, dil listesi geldiğinde kuruluyor.
    initialValues: { name: "", view: 1, isFeatured: false },
    validate: {
      name: (value) => (value.trim() ? null : "TR adı gerekli"),
    },
  });

  useEffect(() => {
    if (!opened) {
      return;
    }
    // Yeni kayıtta da setValues: form.reset() initialValues'a dönüyor ve orada çeviri alanları
    // yok, yani Mantine o alanları kontrolsüz input sayardı. Dil listesi sonradan geldiği için
    // bağımlılıkta da duruyor.
    form.setValues({
      name: category?.name ?? "",
      ...flattenTranslations(
        category?.translations,
        ["name"],
        contentLanguages,
      ),
      view: category?.view ?? 1,
      isFeatured: Boolean(category?.isFeatured),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, category, contentLanguages]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        name: values.name.trim(),
        translations: buildTranslations(values, ["name"], contentLanguages),
        view: values.view,
        isFeatured: values.isFeatured,
      };
      return isEdit
        ? updateCategory(category.id, payload)
        : createCategory(payload);
    },
    onSuccess: () => {
      notify.success(isEdit ? "Kategori güncellendi" : "Kategori oluşturuldu");
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      onClose();
    },
    onError: () => notify.error("İşlem başarısız"),
  });

  // TR adı bir kerede tüm dillere çevirir (Workers AI).
  const [translating, setTranslating] = useState(false);
  const translateNames = async () => {
    const src = form.values.name.trim();
    if (!src) {
      return;
    }
    setTranslating(true);
    try {
      // Çevrilen şey kategori ADI; ürün adı/kategori bağlamı yok, kaynağın kendisi zaten o.
      const results = await Promise.all(
        contentLanguages.map((lang) =>
          translate(src, lang.code, { field: "name" }),
        ),
      );
      contentLanguages.forEach((lang, index) => {
        if (results[index]) {
          form.setFieldValue(`name${lang.code.toUpperCase()}`, results[index]);
        }
      });
      notify.success("Diğer dillere çevrildi");
    } catch {
      notify.error("Çeviri başarısız");
    } finally {
      setTranslating(false);
    }
  };

  const canSubmit = Boolean(form.values.name.trim());

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      size="lg"
      padding="lg"
      title={
        <Group gap="sm">
          <ThemeIcon variant="light" color="chocolate" radius="md" size="lg">
            <IconCategory size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600}>
              {isEdit ? "Kategori Düzenle" : "Yeni Kategori"}
            </Text>
            <Text size="xs" c="dimmed">
              {isEdit
                ? "Kategori bilgilerini güncelle"
                : "Yeni bir kategori oluştur"}
            </Text>
          </div>
        </Group>
      }
    >
      <form
        onSubmit={form.onSubmit(
          (values) => mutation.mutate(values),
          // Zorunlu alan taban dilde ve artık bir sekmenin arkasında; hata başka bir dil
          // seçiliyken görünmez olurdu.
          () => setLang(baseCode),
        )}
      >
        <Stack gap="md">
          <FormSection
            icon={IconCategory}
            title="Kategori Adı"
            description="Türkçe baz; diğer diller menüde o dil seçilince gösterilir"
            action={
              <Button
                variant="light"
                size="xs"
                color="chocolate"
                leftSection={<IconLanguage size={16} />}
                loading={translating}
                onClick={translateNames}
                disabled={
                  !form.values.name.trim() || contentLanguages.length === 0
                }
                title={
                  contentLanguages.length === 0
                    ? "Menüde tek dil açık, çevrilecek dil yok"
                    : undefined
                }
              >
                Diğer Dillere Çevir
              </Button>
            }
          >
            {/* Taban dil ve çeviriler TEK şeritte: her dil için ayrı alan basmak
                            kiracı otuz dil açabildiği için pencereyi duvara çeviriyordu, taban
                            alanını ayrı tutmak da aynı işi iki kez göstermek oluyordu. */}
            <TranslationFields
              languages={languages}
              baseCode={baseCode}
              form={form}
              fields={[
                {
                  name: "name",
                  label: "Kategori Adı",
                  placeholder: NAME_PLACEHOLDER,
                },
              ]}
              active={lang}
              onActiveChange={setLang}
            />
          </FormSection>

          <FormSection
            icon={IconLayoutGrid}
            title="Görünüm"
            description="Anasayfa düzeni ve öne çıkarma"
          >
            <Stack gap="sm">
              <NumberInput
                label="Görünüm Sırası"
                description="Anasayfada gösterim düzeni (1-6)"
                min={1}
                max={6}
                clampBehavior="strict"
                {...form.getInputProps("view")}
              />
              <Group
                justify="space-between"
                wrap="nowrap"
                px="sm"
                py={8}
                style={{
                  border: "1px solid var(--mantine-color-default-border)",
                  borderRadius: "var(--mantine-radius-md)",
                }}
              >
                <div>
                  <Text size="sm" fw={500}>
                    Öne Çıkan Kategori
                  </Text>
                  <Text size="xs" c="dimmed">
                    Anasayfada büyük/vurgulu gösterilir
                  </Text>
                </div>
                <Switch
                  color="teal"
                  size="md"
                  {...form.getInputProps("isFeatured", { type: "checkbox" })}
                />
              </Group>
            </Stack>
          </FormSection>
        </Stack>

        <FormModalFooter>
          <Button variant="default" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!canSubmit}
            leftSection={
              isEdit ? <IconDeviceFloppy size={16} /> : <IconPlus size={16} />
            }
          >
            {isEdit ? "Kaydet" : "Oluştur"}
          </Button>
        </FormModalFooter>
      </form>
    </Modal>
  );
}
